import { Box, Stack } from '@mui/material'
import { ColDef, ICellRendererParams } from 'ag-grid-community'
import { getSharedContentReport } from 'api/reports'
import { PLACEHOLDER } from 'components/column-renderers'
import InfinityAgGrid from 'components/table/InfinityAgGrid'
import TableToolbar from 'components/table/TableToolbar'
import { useExportCSV } from 'hooks/useExportCSV'
import { useGridControl } from 'hooks/useGridControl'
import React, { useCallback, useMemo } from 'react'
import { BaseTableRequest, FilterModel, SharedContentReport } from 'types'
import { useGetDataSource } from 'utils'
import { prepareDateRangeParams } from '../../helpers/prepareDateRangeParams'
import {
  applyContentNameFilter,
  getSharingContentName,
  getSharingContentType,
} from '../../helpers/sharingColumns'
import { useDateRange } from '../../hooks/useDateRange'
import { useSharedContentTypeFilter } from '../../hooks/useSharedContentTypeFilter'
import { DateRangeToolbar } from '../DateRangeToolbar/DateRangeToolbar'

export function ReportsSharedContent() {
  const {
    rangeType,
    year,
    month,
    selectedDate,
    dateInterval,
    onChangeDateInterval,
    onChangeRange,
    onChangeYear,
    onChangeMonth,
    setSelectedDate,
  } = useDateRange()

  const { sharedContentTypeFilter, sharedContentTypeFilterValue } = useSharedContentTypeFilter()

  const getRequest = useCallback(
    (req: BaseTableRequest) => {
      const dateParams = prepareDateRangeParams(rangeType, year, month, selectedDate, dateInterval)

      let filter: FilterModel = {
        ...(req.filter || {}),
      }

      if (filter.contentName) {
        filter = applyContentNameFilter(filter)
        delete filter.contentName
      }

      if (sharedContentTypeFilterValue) {
        filter = { ...filter, ...sharedContentTypeFilterValue }
      }

      return getSharedContentReport({ ...req, filter, params: dateParams })
    },
    [dateInterval, month, rangeType, selectedDate, year, sharedContentTypeFilterValue],
  )

  const getDataSource = useGetDataSource(getRequest)
  const { onGridReady, gridApi, columnApi } = useGridControl(getDataSource)

  const columns: ColDef[] = useMemo(() => {
    return [
      {
        headerName: 'Name',
        field: 'contentName',
        filter: 'agTextColumnFilter',
        cellRenderer: (props: ICellRendererParams<SharedContentReport>): string => {
          if (!props.data) return PLACEHOLDER
          return getSharingContentName(props.data)
        },
      },
      {
        headerName: 'Type',
        field: 'contentType',
        cellRenderer: (props: ICellRendererParams<SharedContentReport>): string => {
          if (!props.data) return PLACEHOLDER
          return getSharingContentType(props.data)
        },
      },
      { headerName: 'Trainee Shares', field: 'traineeShareCount' },
      { headerName: 'Family Shares', field: 'familyShareCount' },
    ]
  }, [])

  const csvFields = useMemo(() => {
    return [
      {
        label: 'Name',
        value: (data: SharedContentReport) => getSharingContentName(data),
      },
      {
        label: 'Type',
        value: (data: SharedContentReport) => getSharingContentType(data),
      },
      {
        label: 'Trainee Shares',
        value: 'traineeShareCount',
      },
      {
        label: 'Family Shares',
        value: 'familyShareCount',
      },
    ]
  }, [])

  const { csvLoading, onExportCSV } = useExportCSV(
    getRequest,
    gridApi,
    columnApi,
    csvFields,
    'Shared Content Report.csv',
  )

  return (
    <>
      <TableToolbar
        left={
          <Stack flexDirection="row" className="gap-16">
            <DateRangeToolbar
              rangeType={rangeType}
              year={year}
              month={month}
              dateInterval={dateInterval}
              onChangeDateInterval={onChangeDateInterval}
              selectedDate={selectedDate}
              onChangeRange={onChangeRange}
              onChangeYear={onChangeYear}
              onChangeMonth={onChangeMonth}
              setSelectedDate={setSelectedDate}
            />

            <Box minWidth="250px">{sharedContentTypeFilter}</Box>
          </Stack>
        }
        exportCSVEnable
        onExportCSV={onExportCSV}
        csvLoading={csvLoading}
      />

      <Box mb={2} height="100%">
        <InfinityAgGrid pagination columnDefs={columns} onGridReady={onGridReady} />
      </Box>
    </>
  )
}
